import { api } from "./base-config";
import { MedicationEntryType } from "model/medication-entry.model";
import { mapToMedicationEntriesDTO } from "api/medication.api";
import { Prescription, PrescriptionType } from "model/prescription.model";
import { mapStringToPrescriptionStatusTypeEnum } from "common/enums/PrescriptionStatusTypeEnum";

/** ---- API ---- */
export const prescriptionGenerate = (
  patientId: number,
  caregiverId: number,
  medicationEntries: Array<MedicationEntryType>
): Promise<Array<PrescriptionType>> => {
  return api
    .post(
      `/resources/secured/rs/caregivers/${caregiverId}/patients/${patientId}/prescriptions/generate`,
      mapToMedicationEntriesDTO(medicationEntries)
    )
    .then(response => mapToPrescriptions(response.data));
};

export const createAllOnRecipe = (
  patientId: number,
  caregiverId: number,
  prescriptionIds: Array<number>,
  stsToken: string,
  keystorePassword: string
): Promise<Array<PrescriptionType>> => {
  return api
    .post(
      `/resources/secured/rs/caregivers/${caregiverId}/patients/${patientId}/prescriptions/createAllOnRecipe`,
      {
        "@type": "CreateOnRecipeRequest",
        prescriptionIds: prescriptionIds,
        stsToken: stsToken,
        keystorePassword: keystorePassword
      }
    )
    .then(response => mapToPrescriptions(response.data));
};

const mergePrescriptions = (
  prescriptions: Array<PrescriptionType>,
  data: [{ id: number; pdf: string }]
): Array<PrescriptionType> => {
  return prescriptions.map(p => {
    const dataPdf = data.filter(d => d.id === p.id)[0];
    return Object.assign({}, p, { pdf: dataPdf ? dataPdf.pdf : "" });
  });
};

export const generatePdf = (
  patientId: number,
  caregiverId: number,
  prescriptions: Array<PrescriptionType>
): Promise<Array<PrescriptionType>> => {
  return api
    .post(
      `/resources/secured/rs/caregivers/${caregiverId}/patients/${patientId}/prescriptions/all/pdf`,
      { prescriptionIds: prescriptions.map(p => p.id) }
    )
    .then(response => mergePrescriptions(prescriptions, response.data));
};

export const createPrescriptions = async (
  patientId: number,
  caregiverId: number,
  prescriptions: Array<PrescriptionType>
): Promise<Array<PrescriptionType>> => {
  const prescriptionsCreated: Array<PrescriptionType> = [];
  for (const prescription of prescriptions) {
    await createPrescription(
      patientId,
      caregiverId,
      prescription
    ).then(prescriptionCreated =>
      prescriptionsCreated.push(prescriptionCreated)
    );
  }
  return Promise.resolve(prescriptionsCreated);
};

const createPrescription = (
  patientId: number,
  caregiverId: number,
  prescription: PrescriptionType
): Promise<PrescriptionType> => {
  return api
    .post(
      `/resources/secured/rs/caregivers/${caregiverId}/patients/${patientId}/prescriptions/`,
      Object.assign(prescription.dto, {
        subContactsIDs: prescription.subContactsIDs
      })
    )
    .then(response => mapToPrescription(response.data));
};

/** ---- Mapper ---- */
const mapToPrescriptions = (
  dtos: Array<PrescriptionDTO>
): Array<PrescriptionType> => {
  return dtos.map(dto => mapToPrescription(dto));
};

const mapToPrescription = (dto: PrescriptionDTO): PrescriptionType => {
  return new Prescription({
    id: dto.id,
    subContactsIDs: dto.subContactsIDs,
    recipeId: dto.recipeId,
    recipeStatus: mapStringToPrescriptionStatusTypeEnum(dto.recipeStatus),
    helenaDate: dto.helenaDate,
    dto: dto
  });
};

interface PrescriptionDTO {
  readonly "@type": "Prescription";
  readonly subContactsIDs: Array<number>;
  readonly id: number;
  readonly recipeId: string;
  readonly recipeStatus: string;
  readonly helenaDate: Date;
}
